import React, { useEffect, useState } from 'react'
import './BrandsManager.sass'
import { withRouter } from 'react-router-dom'
import { createForm } from '../../../../utils/newforms/createForm'
import getFieldRenderObject from '../../../../utils/newforms/render/getFieldRenderObject'
import FormRender from '../../../../utils/newforms/render/FormRender'
import PhotoManager from '../../../PhotoManager/PhotoManager'
import syncForm from '../../../../utils/newforms/changed/syncForm'
import ModulePanel from '../../../../ui/ModulePanel/ModulePanel'
import getDoc from '../../../../utils/db/getDoc'
import Button from '../../../../ui/Button/Button'
import isFormValid from '../../../../utils/newforms/validation/isFormValid'
import StatusMessage, { useStatusMessage } from '../../../../ui/StatusMessage/StatusMessage'
import getFormValues from '../../../../utils/newforms/getFormValues'
import { isNull, omit } from 'lodash'
import uploadPhotos from '../../../../_deprecated/Nauka/NaukaManager/functions/uploadPhotos'
import Spinner from '../../../../ui/Spinner/Spinner'
import getOriginCountiesOptions from '../../../../api/localDbMethods/getOriginCountiesOptions'
import getCollection from '../../../../utils/db/getCollection'
import { updateDoc } from '../../../../utils/db/updateDoc'
import getBrandsInactiveReasonOptions from './BrandsPanel/getBrandsInactiveReasonOptions'
import { isValidBrandSlug, slugifyBrandName } from './brandSlug'
import saveBrandSlug from '../../../../api/products/saveBrandSlug'
import Switch from '../../../../ui/Switch/Switch'
import fieldChangedHandler from '../../../../utils/newforms/changed/fieldChangedHandler'
import {
  BRANDS_SEO_OG_STORAGE_PATH,
  BrandSeoSection,
  FULL_LANDING_SEO_FIELDS,
  FullLandingSeoForm,
  buildSeoDotPathUpdate,
  deleteSeoOgStorageFile,
  resolveOgImageForSave,
  seoFormDefaultsFromDoc,
} from '../../seo'

function BrandsManager({ ...router }) {
  const brandId = router.match.params.brandId

  const [form, setForm] = useState(null)
  const [seoForm, setSeoForm] = useState(null)
  const [ogImage, setOgImage] = useState(null)
  const [previousOgImage, setPreviousOgImage] = useState(null)
  const [showErrors] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useStatusMessage()

  useEffect(() => {
    if (!brandId) {
      setForm(null)
      setSeoForm(null)
      setOgImage(null)
      setPreviousOgImage(null)
      return undefined
    }

    let cancelled = false
    setForm(null)
    setSeoForm(null)
    setOgImage(null)
    setPreviousOgImage(null)

    getDoc({ path: 'brands', docId: brandId, docIdName: 'id' }).then((data) => {
      if (cancelled || !data) return
      const seoFormData = seoFormDefaultsFromDoc(data.seo, { fields: FULL_LANDING_SEO_FIELDS })
      setForm(
        createForm({
          formPattern: new BrandForm(),
          formData: data,
        })
      )
      setSeoForm(
        createForm({
          formPattern: new FullLandingSeoForm(),
          formData: seoFormData,
        })
      )
      setOgImage(seoFormData.ogImage || null)
      setPreviousOgImage(seoFormData.ogImage || null)
    })

    return () => {
      cancelled = true
    }
  }, [brandId])

  const onSubmit = async () => {
    if (!brandId || !form || !seoForm) return

    if (!isFormValid({ form })) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Будь ласка, заповніть усі поля форми',
        closeAfter: 5000,
      })
    }

    setIsLoading(true)
    setStatusMessage({ show: false, type: '', message: '', closeAfter: null })

    let uploadedOrphan = null

    try {
      const data = getFormValues({ form })
      const slug = String(data.slug || '').trim()

      if (!isValidBrandSlug(slug)) {
        setIsLoading(false)
        return setStatusMessage({
          show: true,
          type: 'fail',
          message: 'Некоректний slug. Використовуйте латиницю, цифри та дефіси.',
          closeAfter: 5000,
        })
      }

      const slugResult = await saveBrandSlug({ brandId, slug })
      if (!slugResult.ok) {
        setIsLoading(false)
        return setStatusMessage({
          show: true,
          type: 'fail',
          message: slugResult.message || 'Не вдалося зберегти slug бренду.',
          closeAfter: 5000,
        })
      }

      const values = {
        ...omit(data, ['slug', 'slugNeedsReview', 'slugReviewReason']),
        updated: new Date(),
        photos: data.photos.filter((p) => !p.needUpload).map((p) => omit(p, ['file', 'fileUrl'])),
      }
      const prevBrandData = await getDoc({ path: 'brands', docId: brandId, docIdName: 'id' })

      const brandResult = await updateDoc({ path: 'brands', docId: brandId, data: values })
      if (brandResult === 'fail') {
        throw new Error('brand update failed')
      }

      await uploadPhotos({
        photos: data.photos,
        docId: brandId,
        docPath: 'brands',
        storagePath: 'brands',
      })

      if (prevBrandData.isActive !== values.isActive) {
        const brandCategories = await getCollection({
          path: 'categories',
          docIdName: 'id',
          whereQueries: [{ fieldPath: 'brandId', op: '==', value: brandId }],
        })
        const brandCategoriesIds = brandCategories.map((c) => c.id)
        const productsByCategories =
          brandCategoriesIds.length > 0
            ? await getCollection({
                path: 'products',
                docIdName: 'id',
                whereQueries: [
                  {
                    fieldPath: 'categoryId',
                    op: 'in',
                    value: brandCategoriesIds,
                  },
                ],
              })
            : []

        await Promise.all([
          ...brandCategoriesIds.map((c) =>
            updateDoc({
              path: 'categories',
              docId: c,
              data: { isActive: values.isActive },
            })
          ),
          ...productsByCategories.map((p) =>
            updateDoc({
              path: 'products',
              docId: p.id,
              data: { isActive: values.isActive },
            })
          ),
        ])
      }

      const seoValues = getFormValues({ form: seoForm })
      const { nextOgImage, uploadedOrphan: orphan, previousToDelete } = await resolveOgImageForSave({
        docId: brandId,
        localOgImage: ogImage,
        previousOgImage,
        storagePath: BRANDS_SEO_OG_STORAGE_PATH,
      })
      uploadedOrphan = orphan

      const seoPayload = buildSeoDotPathUpdate(
        {
          ...seoValues,
          ogImage: nextOgImage,
        },
        { fields: FULL_LANDING_SEO_FIELDS }
      )

      const seoResult = await updateDoc({
        path: 'brands',
        docId: brandId,
        data: seoPayload,
      })

      if (seoResult === 'fail') {
        throw new Error('seo update failed')
      }

      if (previousToDelete) {
        await deleteSeoOgStorageFile({
          docId: brandId,
          media: previousToDelete,
          storagePath: BRANDS_SEO_OG_STORAGE_PATH,
        })
      }

      if (!nextOgImage && previousOgImage) {
        await deleteSeoOgStorageFile({
          docId: brandId,
          media: previousOgImage,
          storagePath: BRANDS_SEO_OG_STORAGE_PATH,
        })
      }

      const refreshed = await getDoc({ path: 'brands', docId: brandId, docIdName: 'id' })
      const seoFormData = seoFormDefaultsFromDoc(refreshed?.seo, { fields: FULL_LANDING_SEO_FIELDS })
      setForm(
        createForm({
          formPattern: new BrandForm(),
          formData: refreshed,
        })
      )
      setSeoForm(
        createForm({
          formPattern: new FullLandingSeoForm(),
          formData: seoFormData,
        })
      )
      setOgImage(seoFormData.ogImage || null)
      setPreviousOgImage(seoFormData.ogImage || null)

      setStatusMessage({
        show: true,
        type: 'success',
        message: 'Дані збережено',
        closeAfter: 5000,
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.log('🚀 ~ onSubmit ~ error:', error)
      if (uploadedOrphan) {
        await deleteSeoOgStorageFile({
          docId: brandId,
          media: uploadedOrphan,
          storagePath: BRANDS_SEO_OG_STORAGE_PATH,
        })
      }
      setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Не вдалося зберегти. Спробуйте ще раз.',
        closeAfter: 7000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const onRegenerateSlug = () => {
    if (!form) return
    const values = getFormValues({ form })
    const nextSlug = slugifyBrandName(values.name)
    setForm(
      createForm({
        formPattern: new BrandForm(),
        formData: { ...values, slug: nextSlug },
      })
    )
  }

  return (
    <div className="BrandsManager">
      {!isNull(form) && !isNull(seoForm) ? (
        <>
          {statusMessage.show && (
            <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
          )}
          <ModulePanel title={form.name.values}>
            {form.slugNeedsReview?.values ? (
              <StatusMessage
                className="Site-StatusMessage"
                type="fail"
                message="Перевірте slug бренду: автоматична генерація з назви потребувала уточнення."
              />
            ) : null}
            <div className="ActiveInfo">
              <div className="BrandsManager-Switches">
                {['isActive', 'isFeatured'].map((fieldId) => (
                  <div className="BrandsManager-SwitchRow" key={fieldId}>
                    <Switch
                      checked={form[fieldId].value === true}
                      onChange={(next) =>
                        fieldChangedHandler({ fieldIdentifier: fieldId, setForm })({ target: { value: next } })
                      }
                      disabled={isLoading}
                      ariaLabel={form[fieldId].config.label}
                      title={
                        fieldId === 'isActive'
                          ? 'Дозволяє показувати бренд на сайті'
                          : 'Показує активний бренд у блоці брендів на головній'
                      }
                    />
                    <span>{form[fieldId].config.label}</span>
                  </div>
                ))}
              </div>
              <FormRender
                sections={[
                  {
                    fields: ['inactiveReason'],
                  },
                ]}
                form={form}
                setForm={setForm}
                errors={showErrors}
              />
            </div>
            <div className="MainInfo">
              <div className="BrandsManager-SlugField">
                <FormRender sections={[{ fields: ['slug'] }]} form={form} setForm={setForm} errors={showErrors} />
                <Button
                  className="BrandsManager-SlugRegen"
                  theme="circle"
                  fill="white"
                  icon="sync"
                  tooltip="Перегенерувати slug з назви"
                  onClick={onRegenerateSlug}
                />
              </div>
              <FormRender
                sections={[
                  {
                    fields: ['country', 'description'],
                  },
                ]}
                form={form}
                setForm={setForm}
                errors={showErrors}
              />
              <p className="Title">Фото</p>
              <PhotoManager
                projectId={brandId}
                projectPath="brands"
                formData={form ? form.photos.values : null}
                syncState={(moduleState) =>
                  syncForm({
                    propIdentifier: 'photos',
                    form,
                    setForm,
                    newForm: moduleState,
                  })
                }
              />
            </div>

            <BrandSeoSection form={seoForm} setForm={setSeoForm} ogImage={ogImage} setOgImage={setOgImage} />
          </ModulePanel>
          <div className="Buttons">
            <Button
              title="Зберегти"
              theme="solid"
              fill="accent"
              iconPosition="right"
              size={32}
              isLoading={isLoading}
              onClick={onSubmit}
            />
          </div>
        </>
      ) : (
        <Spinner type="module" />
      )}
    </div>
  )
}

class BrandForm {
  constructor() {
    this.country = {
      field: {
        fieldId: 'country',
        fieldType: 'select',
        getOptions: getOriginCountiesOptions,
        label: 'Країна виробник ',
        required: false,
      },
      render: getFieldRenderObject(),
    }
    this.slug = {
      field: {
        fieldId: 'slug',
        fieldType: 'input',
        label: 'Slug',
        required: true,
        value: '',
      },
      render: getFieldRenderObject(),
    }
    this.slugNeedsReview = {
      field: { fieldId: 'slugNeedsReview', value: false },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.slugReviewReason = {
      field: { fieldId: 'slugReviewReason', value: null },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.description = {
      field: {
        fieldId: 'description',
        fieldType: 'texteditor',
        label: 'Опис бренду',
        required: false,
      },
      render: getFieldRenderObject(),
    }
    this.inactiveReason = {
      field: {
        fieldId: 'inactiveReason',
        fieldType: 'select',
        label: 'Причина деактивації',
        required: true,
        getOptions: getBrandsInactiveReasonOptions(),
      },
      render: getFieldRenderObject({
        byDefault: false,
        ifFieldId: 'isActive',
        value: false,
        dropOnFieldIdValueChanged: 'isActive',
      }),
    }
    this.name = {
      field: { fieldId: 'name' },
      render: getFieldRenderObject({ isSystem: true }),
    }

    this.photos = {
      field: { fieldId: 'photos', value: [] },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.updated = {
      field: { fieldId: 'created', value: new Date() },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.isActive = {
      field: {
        fieldId: 'isActive',
        fieldType: 'checkbox',
        label: 'Активний',
        value: true,
      },
      render: getFieldRenderObject(),
    }
    this.isFeatured = {
      field: {
        fieldId: 'isFeatured',
        fieldType: 'checkbox',
        label: 'Показувати на головній',
        value: false,
      },
      render: getFieldRenderObject(),
    }
  }
}

export default withRouter(BrandsManager)
